/**
 * What if: try an edit, see what it touches, look at it cheaply, then run it properly.
 *
 * The three parts of decision 6.8, in order of what they cost:
 *   1. free      the word-level diff, and who quoted the text you changed - fact about the run you
 *                already have, not a forecast.
 *   2. cheap     a quick look: a real run of eight personas, one round, drawn beside the result and
 *                labelled indicative.
 *   3. committed run it as a new version, then compare with verdicts (decision 6.9).
 *
 * Nothing here estimates a metric. If a number moves, something was run to move it.
 */

import { FlaskConical, Play, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { isFinished } from "@/api/jobs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  type Comparison,
  type Dashboard,
  type Diff,
  type JobView,
  type QuickLook,
  type Study,
  useAddVariant,
  useComparison,
  useDiff,
  useJobs,
  useQuickLook,
  useQuickLookCost,
  useRuns,
  useSubmitJob,
} from "@/lib/queries";

import { DraggablePanel } from "./DraggablePanel";

export function WhatIfPanel({
  study,
  view,
  onClose,
  onQuickLook,
}: {
  study: Study;
  view: Dashboard;
  onClose: () => void;
  /** Hands the quick look's figures to the page, which draws them beside the result. */
  onQuickLook: (result: QuickLook | null) => void;
}) {
  const navigate = useNavigate();
  const working = (study.variants ?? []).find((v) => v.variant_id === study.working_variant_id)
    ?? study.variants?.[study.variants.length - 1];
  const [text, setText] = useState(working?.text ?? "");
  const [jobId, setJobId] = useState<string | null>(null);
  const debounced = useDebounced(text, 400);
  const diff = useDiff(study.study_id, debounced, working?.variant_id ?? null);
  const cost = useQuickLookCost(study.study_id);
  const start = useQuickLook(study.study_id);
  const addVariant = useAddVariant(study.study_id);
  const submit = useSubmitJob();
  const jobs = useJobs(study.study_id).data ?? [];
  const job = jobs.find((j) => j.job_id === jobId);
  const changed = diff.data?.changed ?? false;

  // The page draws the figures as soon as the quick look lands, and clears them when the panel goes.
  useEffect(() => {
    if (job?.status === "done" && job.result) onQuickLook(job.result as unknown as QuickLook);
  }, [job?.status, job?.result, onQuickLook, job]);
  useEffect(() => () => onQuickLook(null), [onQuickLook]);

  const exposure = useMemo(() => exposed(view, working?.text ?? "", debounced), [view, working?.text, debounced]);
  const busy = jobs.some((j) => !isFinished(j));

  async function commit() {
    try {
      const variant = await addVariant.mutateAsync({ text, label: `What if: edit of ${working?.label ?? "v1"}` });
      const started = await submit.mutateAsync({ studyId: study.study_id, variantId: variant.variant_id });
      toast.success("Running the edit properly - the Result appears when it finishes");
      onQuickLook(null);
      onClose();
      navigate(`/studies/${study.study_id}`);
      return started;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "could not start the run");
    }
  }

  return (
    <DraggablePanel title="What if" storageKey="what-if-panel" onClose={onClose} width={480}>
      <div className="space-y-4 text-sm">
        <section className="space-y-2">
          <label htmlFor="what-if-text" className="font-medium">
            1 · Edit the message
          </label>
          <Textarea id="what-if-text" rows={6} value={text} onChange={(e) => setText(e.target.value)} />
          {diff.data && changed && <DiffView diff={diff.data} />}
          {!changed && <p className="text-xs text-muted-foreground">Change the text to see what it touches.</p>}
        </section>

        {changed && <Exposure exposure={exposure} total={view.n} />}

        <section className="space-y-2 border-t border-white/10 pt-3">
          <p className="font-medium">2 · Quick look</p>
          <p className="text-xs text-muted-foreground">
            A real run of {cost.data?.personas ?? 8} of this study&apos;s personas, one round, on the cheapest model
            {cost.data && cost.data.reserve_usd > 0
              ? `, about $${cost.data.likely_high_usd.toFixed(2)} of credit.`
              : ", free in a mock study."}{" "}
            Indicative only: too few people to trust a small difference.
          </p>
          <Button
            size="sm"
            disabled={!changed || busy || start.isPending || cost.data?.affordable === false}
            onClick={() =>
              start.mutate(
                { text, base_variant_id: working?.variant_id },
                {
                  onSuccess: (started: JobView) => setJobId(started.job_id),
                  onError: (error: unknown) =>
                    toast.error(error instanceof Error ? error.message : "could not start the quick look"),
                },
              )
            }
          >
            <FlaskConical aria-hidden="true" />
            {job && !isFinished(job) ? "Running…" : "Take a quick look"}
          </Button>
          {job && !isFinished(job) && (
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {job.stage || "queued…"}
            </p>
          )}
          {job?.status === "failed" && (
            <Alert variant="destructive">
              <AlertDescription>{job.error ?? "the quick look failed"}</AlertDescription>
            </Alert>
          )}
          {job?.status === "done" && job.result && (
            <QuickLookFigures result={job.result as unknown as QuickLook} view={view} />
          )}
        </section>

        <section className="space-y-2 border-t border-white/10 pt-3">
          <p className="font-medium">3 · Run it properly</p>
          <p className="text-xs text-muted-foreground">
            Saves the edit as a new version and runs it on the whole audience. That result is the one to trust, and
            it can then be compared with this one.
          </p>
          <Button size="sm" variant="outline" disabled={!changed || busy || addVariant.isPending}
                  onClick={() => void commit()}>
            <Play aria-hidden="true" /> Run as a new version
          </Button>
        </section>

        <Compare study={study} thisRun={view.run_id} />
      </div>
    </DraggablePanel>
  );
}

const VERDICTS: Record<string, string> = {
  better: "text-step-done",
  worse: "text-[#ff8f75]",
  "within noise": "text-muted-foreground",
};

/** Two finished results side by side, with the verdict rule of decision 6.9 (not this panel's). */
function Compare({ study, thisRun }: { study: Study; thisRun: string }) {
  const runs = (useRuns(study.study_id).data ?? []).filter((r) => r.status === "done");
  const others = runs.filter((r) => r.run_id !== thisRun);
  const [against, setAgainst] = useState<string | null>(null);
  const comparison = useComparison(study.study_id, against, thisRun);

  if (!others.length) {
    return (
      <section className="space-y-1 border-t border-white/10 pt-3">
        <p className="font-medium">4 · Compare</p>
        <p className="text-xs text-muted-foreground">
          There is only one result so far. Run an edit, or re-run this text to measure how much results move by
          chance, and they can be compared here.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-2 border-t border-white/10 pt-3">
      <p className="font-medium">4 · Compare</p>
      <label className="block text-xs text-muted-foreground" htmlFor="compare-with">
        Compare this result against (the &ldquo;before&rdquo;)
      </label>
      <select
        id="compare-with"
        className="h-8 w-full rounded-md border border-white/10 bg-[#0f1115] px-2 text-xs"
        value={against ?? ""}
        onChange={(event) => setAgainst(event.target.value || null)}
      >
        <option value="">Choose a result…</option>
        {others.map((run) => (
          <option key={run.run_id} value={run.run_id}>
            {label(study, run.variant_id)}
            {run.created_at ? ` · ${new Date(run.created_at).toLocaleDateString()}` : ""}
          </option>
        ))}
      </select>
      {comparison.data && <Verdicts comparison={comparison.data} />}
    </section>
  );
}

function label(study: Study, variantId: string): string {
  return (study.variants ?? []).find((v) => v.variant_id === variantId)?.label ?? variantId;
}

function Verdicts({ comparison }: { comparison: Comparison }) {
  return (
    <div className="space-y-2">
      {comparison.same_text && (
        <p className="text-xs text-muted-foreground">
          Both results use the same text, so any difference here is run-to-run variation.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Change is from <span className="text-foreground">{comparison.label_a}</span> to{" "}
        <span className="text-foreground">{comparison.label_b}</span>, the result on screen.
      </p>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th scope="col" className="text-left font-medium">Measure</th>
            <th scope="col" className="text-right font-medium">{comparison.label_a}</th>
            <th scope="col" className="text-right font-medium">{comparison.label_b}</th>
            <th scope="col" className="text-right font-medium">Change</th>
            <th scope="col" className="text-right font-medium">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {comparison.deltas.map((delta) => (
            <tr key={delta.metric}>
              <td className="py-0.5">{delta.label}</td>
              <td className="py-0.5 text-right tabular-nums">{delta.base_text}</td>
              <td className="py-0.5 text-right tabular-nums">{delta.candidate_text}</td>
              <td className="py-0.5 text-right tabular-nums" title={`95% CI ${delta.interval_text}`}>
                {delta.delta_text}
              </td>
              <td className={`py-0.5 text-right ${VERDICTS[delta.verdict]}`}>{delta.verdict}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground">
        Paired over {comparison.n_paired} personas who posted in both: {comparison.moved_up} moved up,{" "}
        {comparison.moved_down} moved down.{" "}
        {comparison.noise_floor
          ? "Better or worse needs the 95% interval to exclude zero and the change to beat the noise floor."
          : "No noise check yet for that text, so verdicts rest on the 95% interval alone - re-run the unchanged text to measure how much results move by chance."}
      </p>
      {comparison.flips.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">
            {comparison.flips.length} personas changed category
          </summary>
          <ul className="mt-1 space-y-1">
            {comparison.flips.map((flip) => (
              <li key={flip.persona_id} className="text-xs">
                <span className="text-foreground">{flip.persona_id}</span>{" "}
                {flip.direction === "up" ? "moved up" : "moved down"}
                {flip.candidate_reasons[0] && (
                  <span className="block text-muted-foreground">{flip.candidate_reasons[0]}</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function DiffView({ diff }: { diff: Diff }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">
        {diff.words_removed} words removed, {diff.words_added} added.
      </p>
      <p className="max-h-32 overflow-y-auto rounded-lg border border-white/10 bg-black/30 p-2 text-xs leading-relaxed">
        {diff.parts.map((part, index) =>
          part.kind === "equal" ? (
            <span key={index}>{part.text}</span>
          ) : part.kind === "delete" ? (
            <del key={index} className="bg-[#bf3f37]/25 text-[#ff8f75] no-underline line-through">{part.text}</del>
          ) : (
            <ins key={index} className="bg-[#2b8ae8]/25 text-[#a5d2ff] no-underline">{part.text}</ins>
          ),
        )}
      </p>
    </div>
  );
}

type Exposed = { persona_id: string; name: string; stance_label: string; category: string; quotes: string[] };

/** Who quoted the text you changed. Fact about the run already on screen - nothing is predicted. */
function exposed(view: Dashboard, before: string, after: string): Exposed[] {
  if (!before || !after || before.trim() === after.trim()) return [];
  const remaining = normalise(after);
  return view.posts
    .map((post) => ({
      persona_id: post.persona_id,
      name: post.name,
      stance_label: post.stance_label,
      category: post.category,
      quotes: post.quoted.filter((quote) => {
        const needle = normalise(quote);
        return needle.length > 12 && normalise(before).includes(needle) && !remaining.includes(needle);
      }),
    }))
    .filter((persona) => persona.quotes.length > 0);
}

const normalise = (text: string) => text.replace(/\s+/g, " ").trim().toLowerCase();

function Exposure({ exposure, total }: { exposure: Exposed[]; total: number }) {
  if (!total) return null;
  const negative = exposure.filter((p) => p.category === "negative").length;
  return (
    <section className="space-y-2 rounded-lg border border-white/10 bg-black/30 p-2">
      <p className="text-xs font-medium">What your edit touches</p>
      {exposure.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nobody quoted the words you changed. That does not mean the edit has no effect - only that these personas
          did not cite that text.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            <span className="text-foreground">
              {exposure.length} of {total} personas
            </span>{" "}
            ({Math.round((exposure.length / total) * 100)}%) quoted text you changed
            {negative > 0 && `, ${negative} of them negative`}.
          </p>
          <ul className="max-h-40 space-y-1 overflow-y-auto">
            {exposure.map((persona) => (
              <li key={persona.persona_id} className="text-xs">
                <span className="text-foreground">
                  {persona.persona_id} · {persona.name}
                </span>{" "}
                <span className="text-muted-foreground">({persona.stance_label})</span>
                <span className="block text-muted-foreground">“{persona.quotes[0]}”</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function QuickLookFigures({ result, view }: { result: QuickLook; view: Dashboard }) {
  const tiles = view.tiles.filter((t) => t.key in result.metrics.values);
  return (
    <div className="space-y-1 rounded-lg border border-step-next/40 bg-step-next/5 p-2">
      <p className="flex items-center gap-1 text-xs font-medium text-step-next">
        <Sparkles className="size-3" aria-hidden="true" /> Indicative: {result.personas} personas, {result.rounds}{" "}
        round
      </p>
      <table className="w-full text-xs">
        <tbody>
          {tiles.map((tile) => {
            const now = tile.value ?? 0;
            const then = result.metrics.values[tile.key];
            return (
              <tr key={tile.key}>
                <td className="py-0.5 text-muted-foreground">{tile.label}</td>
                <td className="py-0.5 text-right tabular-nums">{tile.formatted}</td>
                <td className="py-0.5 text-right tabular-nums text-step-next">{format(tile.key, then)}</td>
                <td className="py-0.5 pl-2 text-right tabular-nums text-muted-foreground">{change(tile.key, then - now)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground">
        Eight people cannot settle a small difference. Run it properly before acting on this.
      </p>
    </div>
  );
}

const PERCENT = new Set(["positive_share", "negative_share", "backlash_share"]);
const format = (key: string, value: number) =>
  PERCENT.has(key) ? `${Math.round(value * 100)}%` : value.toFixed(2);
const change = (key: string, delta: number) =>
  PERCENT.has(key) ? `${delta >= 0 ? "+" : ""}${Math.round(delta * 100)} pp` : `${delta >= 0 ? "+" : ""}${delta.toFixed(2)}`;

function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}
